import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DATA_TAG_DUPLICATE_DRIVE_DATA,
  FETCH_DATA_TAG_DRIVES,
} from "../DataTagConstants";
import "./DataEstateDrives.scss";
import { DriveSensitivityBadge } from "./DriveSensitivityBadge";
import TablePagination from "@material-ui/core/TablePagination";
import QuickFilters from "./QuickFilters";
import DriveDrawer from "./DriveDrawer";

export const categoryIcons = {
  ORGANIZATIONAL: "/dataOwnerViewIcons/categoryIcons/organizationalIcon.svg",
  PERSONAL: "/dataOwnerViewIcons/categoryIcons/personalIcon.svg",
  API: "/dataOwnerViewIcons/categoryIcons/API_SensitivityIcon.svg",
  FINANCIAL: "/dataOwnerViewIcons/categoryIcons/financialIcon.svg",
  MARKETING: "/dataOwnerViewIcons/categoryIcons/marketingIcon.svg",
  MEDICAL: "/dataOwnerViewIcons/categoryIcons/medicalIcon.svg",
  OTHER: "/dataOwnerViewIcons/categoryIcons/Other_SensitivityIcon.svg",
  NO_SENSITIVITY: "/dataOwnerViewIcons/categoryIcons/Other_SensitivityIcon.svg",
  NOT_PROCESSED: "/dataOwnerViewIcons/categoryIcons/Other_SensitivityIcon.svg",
};

export const FIXED_CATEGORIES = [
  "PERSONAL",
  "MARKETING",
  "FINANCIAL",
  "ORGANIZATIONAL",
  "MEDICAL",
  "API",
  "NO_SENSITIVITY",
  "OTHER",
  "NOT_PROCESSED",
];

export const categoryNames = {
  PERSONAL: "Personal",
  MARKETING: "Marketing",
  FINANCIAL: "Financial",
  ORGANIZATIONAL: "Organizational",
  MEDICAL: "Medical",
  API: "API",
  NO_SENSITIVITY: "No Sensitivity",
  OTHER: "Other",
  NOT_PROCESSED: "Not Processed",
};

function CategoryBreakdown({ typeCounts = [] }) {
  const countMap = typeCounts.reduce((acc, item) => {
    acc[item.type] = item.count;
    return acc;
  }, {});

  const ordered = useMemo(() => {
    return FIXED_CATEGORIES.map((cat) => ({ cat, count: countMap[cat] || 0 }))
      .filter((i) => i.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [typeCounts]);

  const visible = ordered.slice(0, 3);
  const remaining = ordered.slice(3);

  return (
    <div className="category-breakdown">
      {visible.map((item) => (
        <div
          key={item.cat}
          className="category-row"
          title={categoryNames[item.cat]}
        >
          <img src={categoryIcons[item.cat]} className="category-icon" alt="" />
          <span className="category-count">{item.count}</span>
        </div>
      ))}
      {remaining.length > 0 && (
        <div className="category-row remaining-wrapper">
          <div className="remaining-count">+{remaining.length}</div>
          <div className="remaining-tooltip">
            {remaining.map((item) => (
              <div key={item.cat} className="tooltip-row">
                <img
                  src={categoryIcons[item.cat]}
                  className="tooltip-icon"
                  alt=""
                />
                <span className="tooltip-name">{categoryNames[item.cat]}</span>
                <span className="tooltip-count">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DuplicateSummaryStrip({ dupInfo }) {
  if (!dupInfo) return null;

  return (
    <div className="dup-summary-strip">
      <div className="dup-summary-item duplicate">
        <img
          src="/DataTagIcons/files.svg"
          className="dup-summary-icon"
          alt=""
        />
        <span className="data-estate-drive-sensitivity-title">Duplicate Objects : </span>
        <span className="dup-summary-value">{dupInfo.filesDuplicated}</span>
      </div>

      <div className="dup-summary-item mutation">
        <img
          src="/DataTagIcons/file-mutation.svg"
          className="dup-summary-icon"
          alt=""
        />
        <span className="data-estate-drive-sensitivity-title">Copies: </span>
        <span className="dup-summary-value">{dupInfo.mutationCount}</span>
      </div>
    </div>
  );
}

function DataEstateDrives({ cardsPerRow = 4 }) {
  const dispatch = useDispatch();

  const drivesData = useSelector(
    (state) => state.dataTagReducer.drives?.content || [],
  );
  const totalCount = useSelector(
    (state) => state.dataTagReducer.drives?.totalElements || 0,
  );
  const dataSourceId = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.id,
  );
  const summaryDataRaw = useSelector(
    (state) => state.dataTagReducer.categoryCardData,
  );
  const duplicateDriveData = useSelector(
    (state) => state.dataTagReducer.duplicateDriveData || [],
  );

  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedSensitivity, setSelectedSensitivity] = useState([]);
  const [categoryFilter, setSelectedCategoryFilter] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeDrive, setActiveDrive] = useState(null);

  const dupLookup = useMemo(() => {
    return duplicateDriveData.reduce((map, entry) => {
      if (entry?.actor?.id) {
        map[entry.actor.id] = {
          mutationCount: entry.mutationCount,
          filesDuplicated: entry.filesDuplicated,
        };
      }
      return map;
    }, {});
  }, [duplicateDriveData]);

  const handleOpenDrawer = (drive) => {
    setActiveDrive(drive);
    setDrawerOpen(true);
  };
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setActiveDrive(null);
  };

  const handleSelect = (drive) => {
    setSelectedCard(drive.driveName);
    handleOpenDrawer(drive);
  };

  useEffect(() => {
    if (!dataSourceId) return;
    dispatch({
      type: FETCH_DATA_TAG_DRIVES,
      payload: {
        dataSourceId,
        page,
        size: rowsPerPage,
        sensitivity:
          selectedSensitivity.length > 0 ? selectedSensitivity : null,
        category: categoryFilter.length > 0 ? categoryFilter : null,
      },
    });
    dispatch({
      type: DATA_TAG_DUPLICATE_DRIVE_DATA,
      payload: { dataSourceId, page, size: rowsPerPage },
    });
  }, [
    dataSourceId,
    page,
    rowsPerPage,
    selectedSensitivity,
    categoryFilter,
    dispatch,
  ]);

  return (
    <div className="data-estate-drives-wrapper">
      <QuickFilters
        showCategory={summaryDataRaw.length > 0}
        categoryValue={categoryFilter}
        onCategoryChange={setSelectedCategoryFilter}
        sensitivityValue={selectedSensitivity}
        onSensitivityChange={setSelectedSensitivity}
        loc="data-estate-drives"
      />

      <div
        className="drives-grid"
        style={{ gridTemplateColumns: `repeat(${cardsPerRow}, 1fr)` }}
      >
        {drivesData.map((drive, index) => {
          const dupInfo = drive.enterpriseActorId
            ? (dupLookup[drive.enterpriseActorId] ?? null)
            : null;

          return (
            <div
              key={index}
              className={`drive-card ${selectedCard === drive.driveName ? "selected" : ""} ${dupInfo ? "has-duplicates" : ""}`}
              onClick={() => handleSelect(drive)}
            >
              <div className="driveDetails">
                <div className="drive-icon">
                  <img src="/DataTagIcons/DataEstateFolder.svg" alt="folder" />
                </div>

                <div className="driveDetailsSummary">
                  <div className="drive-title">
                    {drive.driveName || "Unnamed Drive"}
                  </div>

                  {/* Files / PDA / Size row */}
                  <div className="drive-summary-container">
                    <div className="drive-files">Files: {drive.totalFiles}</div>
                    <div className="drive-files">PDA: {drive.pdaFiles}</div>
                    <div className="drive-files">
                      Size: {drive.totalFileSize}
                    </div>
                  </div>

                  {/* Duplication strip — only rendered when there's a match */}
                  <DuplicateSummaryStrip dupInfo={dupInfo} />

                  <div>
                    <span className="data-estate-drive-sensitivity-title">
                      Sensitivity
                    </span>
                    <DriveSensitivityBadge drive={drive} />
                  </div>

                  <CategoryBreakdown typeCounts={drive.types} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />

      <DriveDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        drive={activeDrive}
        dupInfo={
          activeDrive
            ? (dupLookup[activeDrive.enterpriseActorId] ?? null)
            : null
        }
        activeDrive={activeDrive}
      />
    </div>
  );
}

export default DataEstateDrives;
